/**
 * Estimate Filters Component
 * BuilderBel Construction Project Management System
 */

import React from 'react';
import { Search, X, Filter } from 'lucide-react';
import type { EstimateStatus, EstimateType, EstimateFilterParams } from '../../types/estimate';

export interface EstimateFiltersProps {
  filters: EstimateFilterParams;
  onFilterChange: (filters: EstimateFilterParams) => void;
}

const statusOptions: { value: EstimateStatus | ''; label: string }[] = [
  { value: '', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'pending_review', label: 'Pending Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'superseded', label: 'Superseded' },
];

const typeOptions: { value: EstimateType | ''; label: string }[] = [
  { value: '', label: 'All Types' },
  { value: 'preliminary', label: 'Preliminary' },
  { value: 'detailed', label: 'Detailed' },
  { value: 'budget', label: 'Budget' },
  { value: 'change_order', label: 'Change Order' },
  { value: 'final', label: 'Final' },
];

const EstimateFilters: React.FC<EstimateFiltersProps> = ({ filters, onFilterChange }) => {
  const activeFilterCount = [filters.status, filters.estimateType, filters.search].filter(
    Boolean
  ).length;

  const handleClearFilters = () => {
    onFilterChange({});
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex flex-wrap items-center gap-3">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search estimates..."
            value={filters.search || ''}
            onChange={e => onFilterChange({ ...filters, search: e.target.value || undefined })}
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
        </div>

        {/* Status Filter */}
        <select
          value={filters.status || ''}
          onChange={e =>
            onFilterChange({
              ...filters,
              status: (e.target.value as EstimateStatus) || undefined,
            })
          }
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white"
        >
          {statusOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        {/* Type Filter */}
        <select
          value={filters.estimateType || ''}
          onChange={e =>
            onFilterChange({
              ...filters,
              estimateType: (e.target.value as EstimateType) || undefined,
            })
          }
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white"
        >
          {typeOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        {/* Filter Count & Clear */}
        {activeFilterCount > 0 && (
          <button
            onClick={handleClearFilters}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Filter className="h-4 w-4" />
            <span>{activeFilterCount} active</span>
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );
};

export default EstimateFilters;
